/* EditorialSelect — fully-controlled dark-surface dropdown.
   Native <select> chrome (text color, caret, open menu options) doesn't
   reliably restyle on dark backgrounds across browsers, so we render our
   own panel. Keeps the Lumetier hairline-underline trigger + warm gold
   accents; supports keyboard nav (↑↓ Enter Esc) and click-outside close. */
import { useEffect, useRef, useState } from "react";
import { ChevronDown, Check } from "lucide-react";

export default function EditorialSelect({
  value,
  onChange,
  options,           // [{ value, label, descriptor?, disabled? }]
  placeholder = "Select…",
  className = "",
  "data-testid": testid,
  id,
}) {
  const [open, setOpen] = useState(false);
  const [focusIdx, setFocusIdx] = useState(-1);
  const rootRef = useRef(null);
  const current = options.find((o) => o.value === value);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    }
    function onKey(e) {
      if (e.key === "Escape") { setOpen(false); return; }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setFocusIdx((i) => Math.min(options.length - 1, i + 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setFocusIdx((i) => Math.max(0, i - 1));
      } else if (e.key === "Enter") {
        e.preventDefault();
        const opt = options[focusIdx];
        if (opt && !opt.disabled) onChange(opt.value);
        setOpen(false);
        // Restore focus to the trigger so subsequent Tab/Shift+Tab behave naturally.
        rootRef.current?.querySelector('button[type="button"]')?.focus();
      }
    }
    document.addEventListener("mousedown", onDocClick);
    // Capture phase: run BEFORE the focused trigger <button> turns Enter into
    // its default click. Otherwise React 18 batches setOpen(false) with the
    // button's setOpen(v => !v) and the toggle wins, keeping the menu open.
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open, focusIdx, options, onChange]);

  return (
    <div ref={rootRef} className={`relative ${className}`} data-testid={testid}>
      <button
        id={id}
        type="button"
        onClick={() => {
          setOpen((v) => !v);
          setFocusIdx(options.findIndex((o) => o.value === value));
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-3 bg-transparent border-0 border-b border-border focus:border-[color:var(--gold-base)] hover:border-foreground/40 transition-colors px-0 py-2 text-left outline-none"
        data-testid={testid ? `${testid}-trigger` : undefined}
      >
        <span className="flex items-baseline gap-2 min-w-0 flex-1">
          {current ? (
            <>
              <span className="font-serif italic text-base text-foreground truncate" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
                {current.label}
              </span>
              {current.descriptor && (
                <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground truncate hidden sm:inline">
                  · {current.descriptor}
                </span>
              )}
            </>
          ) : (
            <span className="text-muted-foreground/60 italic font-serif" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
              {placeholder}
            </span>
          )}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 text-[color:var(--gold-base)] shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <ul
          role="listbox"
          aria-labelledby={id}
          className="absolute z-50 left-0 right-0 top-full mt-1 bg-[#0E0D0C] border border-[color:var(--gold-foil)]/40 shadow-xl shadow-black/40 max-h-[300px] overflow-auto"
          data-testid={testid ? `${testid}-menu` : undefined}
        >
          {options.map((opt, idx) => {
            const selected = opt.value === value;
            const focused = idx === focusIdx;
            return (
              <li
                key={opt.value}
                role="option"
                aria-selected={selected}
                aria-disabled={opt.disabled || undefined}
                onMouseEnter={() => setFocusIdx(idx)}
                onClick={() => {
                  if (opt.disabled) return;
                  onChange(opt.value);
                  setOpen(false);
                }}
                className={`px-4 py-3 cursor-pointer flex items-baseline gap-3 transition-colors border-b border-[color:var(--gold-foil)]/10 last:border-b-0 ${
                  opt.disabled ? "opacity-40 cursor-not-allowed" :
                  focused ? "bg-[color:var(--gold-base)]/15" :
                  "hover:bg-[color:var(--gold-base)]/10"
                }`}
                data-testid={testid ? `${testid}-option-${opt.value}` : undefined}
              >
                <span
                  className="font-serif italic text-base text-[color:var(--cream)] flex-1 min-w-0 truncate"
                  style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                >
                  {opt.label}
                </span>
                {opt.descriptor && (
                  <span className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--cream)]/55 shrink-0">
                    {opt.descriptor}
                  </span>
                )}
                {selected && (
                  <Check className="w-3.5 h-3.5 text-[color:var(--gold-base)] shrink-0" />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
